import { LogOut } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSidebar } from '@/components/ui/sidebar';
import { clearStorage } from '@/shared/utils/session.js';
import { useTranslation } from 'react-i18next';

export function NavUser({ user }) {
  const { t } = useTranslation(['common', 'navigation']);
  const { state, isMobile } = useSidebar();
  const collapsed = state === 'collapsed';

  if (!user) {
    return null;
  }

  const userName = user?.name || user?.firstName || 'User';
  const userEmail = user?.email || 'email@example.com';
  const userAvatar = user?.avatar || user?.photoUrl || '';

  // Get initials for fallback
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="px-3 py-4">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="w-full flex items-center gap-3 rounded-lg p-2 -m-2 transition-all duration-200 cursor-pointer group">
            <Avatar className="h-10 w-10 rounded-full ring-2 ring-transparent group-hover:ring-blue-200 transition-all duration-200">
              <AvatarImage src={userAvatar} alt={userName} />
              <AvatarFallback className="rounded-full bg-blue-100 text-blue-600 group-hover:bg-blue-200 transition-colors duration-200">
                {getInitials(userName)}
              </AvatarFallback>
            </Avatar>
            {!collapsed && (
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors duration-200">
                  {userName}
                </p>
                <p className="text-xs text-gray-500 truncate">{userEmail}</p>
              </div>
            )}
            {!collapsed && (
              <div className="opacity-0 max-md:opacity-100 group-hover:opacity-100 transition-opacity duration-200">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[200px] rounded-lg animate-in fade-in-0 zoom-in-95 data-[side=right]:slide-in-from-left-2 data-[side=left]:slide-in-from-right-2 data-[side=top]:slide-in-from-bottom-2 data-[side=bottom]:slide-in-from-top-2 duration-200"
          side={isMobile ? 'top' : 'right'}
          align="end"
          sideOffset={8}
        >
          <DropdownMenuItem
            onClick={clearStorage}
            className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer transition-colors duration-150"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t('sidebar:logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
